import { Box, Flex, Img } from "@chakra-ui/react"
import ImageGallery from "react-image-gallery"
import "react-image-gallery/styles/css/image-gallery.css"
import { useParams } from "react-router"
import { FALLBACKIMAGES, URLForwardSlashRestore, imageSliderWidth, imageSliderWidth2XL, imageSliderWidthXL } from "../../common/commonConstants"
import { useProductByCodeQuery, type ProductDocs } from "../../type-and-hooks"
import { ErrorMessage } from "../commons/error.main"
import { Loading } from "../commons/loader.main"

// Custom styles for image gallery
const galleryStyles = `
.image-gallery-image {
  object-fit: contain;
  height: ${imageSliderWidth}px !important;
  width: ${imageSliderWidth}px !important;
}
.image-gallery {
  width: ${imageSliderWidth}px !important;
}
.image-gallery-content {
  width: ${imageSliderWidth}px !important;
}
@media (min-width: 1280px) {
  .image-gallery-image {
    height: ${imageSliderWidthXL}px !important;
    width: ${imageSliderWidthXL}px !important;
  }
  .image-gallery { width: ${imageSliderWidthXL}px !important; }
  .image-gallery-content { width: ${imageSliderWidthXL}px !important; }
}
@media (min-width: 1536px) {
  .image-gallery-image {
    height: ${imageSliderWidth2XL}px !important;
    width: ${imageSliderWidth2XL}px !important;
  }
  .image-gallery { width: ${imageSliderWidth2XL}px !important; }
  .image-gallery-content { width: ${imageSliderWidth2XL}px !important; }
}
`

interface ImageSliderProps {
  productCode?: string
}

export const ImageSlider = ({ productCode }: ImageSliderProps): JSX.Element => {
  const { id: productId } = useParams<{ id: string }>()
  // Use the prop if provided, otherwise fall back to URL parameter
  const id = productCode ?? URLForwardSlashRestore(productId)

  const { loading, error, data } = useProductByCodeQuery({ variables: { productCode: id ?? "" }, fetchPolicy: "cache-and-network" })

  interface getImgesOutputInterface {
    original: string
    thumbnail: string
  }

  const getImages = (imagesArray: ProductDocs[] | null | undefined): getImgesOutputInterface[] => {
    const fallBackImage = FALLBACKIMAGES["150x150"]
    if (!imagesArray) return []

    return imagesArray?.map(item => ({
      original: item.thumbnail_url ?? fallBackImage,
      thumbnail: item.thumbnail_url ?? fallBackImage
    }))
  }
  const docGrp: ProductDocs[] = (data?.productByCode?.doc_grp as ProductDocs[]) ?? []
  const images = getImages(docGrp)
  const imageGalleryProps = {
    thumbnailHeight: 50,
    thumbnailWidth: 100,
    showPlayButton: false,
    showFullscreenButton: false,
    additionalClass: "snap-image-gallery",
    originalHeight: imageSliderWidth,
    originalWidth: imageSliderWidth,
    sizes: imageSliderWidth,
    fullScreen: true
  }

  // Calculate total height needed: main image + thumbnails + spacing
  const totalHeight = imageSliderWidth + imageGalleryProps.thumbnailHeight + 50 // 20px for spacing

  return (
    <>
      <style>{galleryStyles}</style>
      <Loading loading={loading} />
      {error && <ErrorMessage error={"Failed to load gallery: ImageSlider."} primaryAction={false} primaryActionText={""} />}

      <Box
        bg="#f3f3f3"
        h={[totalHeight, totalHeight, totalHeight, totalHeight, imageSliderWidthXL + 100, imageSliderWidth2XL + 100]}
        w={["100%", "100%", "100%", imageSliderWidth, imageSliderWidthXL, imageSliderWidth2XL]}
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={["16px", "16px", "32px"]}
        overflow="hidden"
        position="relative"
      >
        {!loading && !error && images.length === 0 && (
          <Flex align="center" justify="center" w="100%" h="100%">
            <Img
              src={FALLBACKIMAGES["150x150"]}
              w={["100%", "100%", "100%", imageSliderWidth]}
              height={["auto", "auto", "auto", imageSliderWidth]}
              alt="placeholder"
              objectFit="contain"
            />
          </Flex>
        )}

        {images.length > 0 && !loading && !error && <ImageGallery {...imageGalleryProps} items={getImages(docGrp)} />}
      </Box>
    </>
  )
}
